import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Spin } from 'antd';
import { refreshSession } from '../../utils/apiRequest';
import { logout } from '../../redux/slice/userSlice';

const FullscreenSpinner = ({ label }: { label?: string }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      gap: 16,
    }}
  >
    <Spin size="large" />
    {label && <div style={{ fontSize: 13, opacity: 0.65 }}>{label}</div>}
  </div>
);

/** Backoff for background reconnect attempts while the server is unreachable. */
const RECONNECT_DELAYS_MS = [2000, 5000, 10000];
const reconnectDelayFor = (attempt: number) =>
  RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)];

/**
 * Runs once, before anything else renders: exchanges the HttpOnly refresh
 * cookie (if any) for a fresh in-memory access token. The access token is
 * never persisted, so it's gone after every hard refresh — this is what
 * gets it back silently instead of bouncing the user to /login.
 *
 * ProtectedRoute / App's `auth` checks must not run until this resolves
 * (success or rejection) — otherwise a logged-in user with a stale persisted
 * `auth: true` flag but no in-memory token yet would flash through routes
 * that immediately 401.
 */
const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ready, setReady] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const dispatch = useDispatch();
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const run = async (attempt: number) => {
      const result = await refreshSession(attempt === 0 ? 'app-load' : `app-load-reconnect-${attempt}`);
      if (cancelled.current) return;

      // Only an explicit rejection (401/403 from /auth/refresh) clears the
      // session. A transient failure means the server was unreachable — the
      // cookie may well still be valid, so logging out here would end a live
      // session every time the dev server restarted mid-load. Do not conflate
      // network/5xx failures with auth rejection.
      if (result === 'rejected') {
        dispatch(logout());
        setReady(true);
        return;
      }

      if (result === 'success') {
        setReconnecting(false);
        setReady(true);
        return;
      }

      // Transient: hold the gate closed, show a non-destructive
      // "Reconnecting…" state, and keep retrying in the background rather
      // than redirecting the user away.
      setReconnecting(true);
      const delay = reconnectDelayFor(attempt);
      console.warn(`[Auth] App-load refresh transient — reconnecting in ${delay}ms (attempt ${attempt + 1})`);
      timer = setTimeout(() => run(attempt + 1), delay);
    };

    run(0);

    return () => {
      cancelled.current = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ready) return <FullscreenSpinner label={reconnecting ? 'Reconnecting…' : undefined} />;
  return <>{children}</>;
};

export default AuthGate;
